import { getValueUnitDisplayLabel } from '#utils/get-value-unit-display-label.ts';
import { FieldType } from '@leagueplatform/dashboard-api';
import { MetricWithConfigId } from '../hooks/use-group-and-format-metrics.hook';

/**
 * Constructs a formatted metric label for display for a single group.
 * @param metrics - Array of metrics from a single group to format
 * @param displayConfigName - Whether to display config names with metric values (used if first metric doesn't have metricGroupingKey)
 * @returns Comma-separated string of formatted metric values
 */
export function constructMetricLabel(
  metrics: MetricWithConfigId[],
  displayConfigName: boolean,
): string {
  const hasGroupingKey = metrics[0]?.metricGroupingKey !== undefined;

  // Calculate displayConfigName locally if the first metric has a metricGroupingKey
  // Otherwise use the provided displayConfigName
  const shouldDisplayConfigName = hasGroupingKey
    ? (() => {
        // Check if there are duplicates or different names
        const names = metrics.map(
          (metric) => metric.metricComponent?.data?.name,
        );
        const uniqueNames = new Set(names);
        // Return true if there are duplicates (same name appears more than once)

        return names.length > uniqueNames.size;
      })()
    : displayConfigName;

  // Filter out boolean metrics with falsy values if there is a grouping key
  const filteredMetrics = hasGroupingKey
    ? metrics.filter((dataMetric) => {
        const healthMetricInputs =
          dataMetric.editInputConfig?.data?.healthMetricInputs;
        if (healthMetricInputs) {
          const hasBooleanFieldWithFalsyValue = healthMetricInputs.some(
            (input) => {
              const hasBooleanField = input.valueFields?.some(
                (field) => field.fieldType === FieldType.BOOLEAN,
              );
              return hasBooleanField && !dataMetric.value;
            },
          );
          if (hasBooleanFieldWithFalsyValue) {
            // Don't display if value is falsy (false, 0, null, undefined, empty string)
            return false;
          }
        }
        return true;
      })
    : metrics;

  const metricValues = filteredMetrics.map((dataMetric) => {
    // Prepare metric value with config name
    const valueLabels = getValueUnitDisplayLabel({
      value: dataMetric.value,
      label: dataMetric.label,
      unitOptions: dataMetric.metricComponent.data.unit,
    });
    const valueString = valueLabels
      .map(([displayValue, displayUnit]) => `${displayValue} ${displayUnit}`)
      .join(' ');
    const configName = shouldDisplayConfigName
      ? ` (${dataMetric.metricComponent.data.name.toLocaleLowerCase()})`
      : '';
    return `${valueString}${configName}`;
  });

  return metricValues.join(', ');
}
